import React, { useMemo } from "react";
import styled from "styled-components";
import { diffLines } from "diff";
import dayjs from "dayjs";

import type { NoteChangePayload } from "../../../app/services/notes";

/**
 * 変更提案（プルリクエスト）の差分表示。
 *
 * - 本文（Markdown ソース）は diff パッケージの行差分で GitHub 風に表示
 * - タイトル・カード属性（カテゴリ / 有効期限 / 必須表示 / ホームに投稿）は
 *   変更があった項目のみ「変更前 → 変更後」で列挙
 */

const DiffBox = styled.div`
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
`;

const DiffPre = styled.pre`
  margin: 0;
  max-height: 480px;
  overflow: auto;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.7;
`;

/* GitHub 準拠の差分配色（デザイントークン外だが差分表示の慣習を優先） */
const DiffLine = styled.div<{ $type: "added" | "removed" | "context" }>`
  padding: 0 12px;
  white-space: pre-wrap;
  word-break: break-all;
  background: ${({ $type }) =>
    $type === "added"
      ? "#e6ffec"
      : $type === "removed"
        ? "#ffebe9"
        : "transparent"};
  color: ${({ $type }) =>
    $type === "removed" ? "var(--soremo-light-gray)" : "var(--soremo-gray)"};
`;

const FieldChangeRow = styled.div.attrs({
  className: "u-row u-gap8 u-items-center bodytext-13",
})`
  flex-wrap: wrap;

  .field-name {
    color: var(--soremo-light-gray);
    flex-shrink: 0;
  }

  .field-old {
    color: var(--soremo-light-gray);
    text-decoration: line-through;
  }

  .field-arrow {
    color: var(--soremo-light-gray);
    font-size: 14px;
  }
`;

interface DiffRow {
  type: "added" | "removed" | "context";
  text: string;
}

const buildDiffRows = (before: string, after: string): DiffRow[] => {
  const rows: DiffRow[] = [];
  for (const part of diffLines(before, after)) {
    const type = part.added ? "added" : part.removed ? "removed" : "context";
    // 末尾の改行で split すると空行が 1 つ余分に出るため除去する
    const lines = part.value.replace(/\n$/, "").split("\n");
    for (const line of lines) {
      rows.push({
        type,
        text: `${type === "added" ? "+" : type === "removed" ? "-" : " "} ${line}`,
      });
    }
  }
  return rows;
};

const formatFieldValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return key === "expires_at" ? "無期限" : "なし";
  }
  if (typeof value === "boolean") return value ? "ON" : "OFF";
  if (key === "expires_at" && typeof value === "string") {
    return dayjs(value).format("YYYY/M/D");
  }
  return String(value);
};

const CARD_FIELD_LABELS: [keyof NoteChangePayload, string][] = [
  ["category", "カテゴリ"],
  ["expires_at", "有効期限"],
  ["is_required", "必須表示"],
  ["is_published", "ホームに投稿"],
];

export interface ChangeDiffProps {
  /** 比較元（新規作成の提案では null = 空とみなす） */
  before: NoteChangePayload | null;
  /** 比較先（提案内容） */
  after: NoteChangePayload;
  /** カード属性の差分も表示するか */
  isCard: boolean;
}

const ChangeDiff: React.FC<ChangeDiffProps> = ({ before, after, isCard }) => {
  const rows = useMemo(
    () => buildDiffRows(before?.content ?? "", after.content ?? ""),
    [before, after],
  );

  const normalize = (value: unknown) =>
    value === null || value === undefined || value === false ? "" : value;
  const titleChanged = (before?.title ?? "") !== after.title;
  const changedFields = isCard
    ? CARD_FIELD_LABELS.filter(([key]) =>
        before
          ? normalize(before[key]) !== normalize(after[key])
          : // 新規作成の提案では初期値（空 / OFF）でない項目のみ列挙する
            normalize(after[key]) !== "",
      )
    : [];

  const contentChanged = rows.some((r) => r.type !== "context");

  return (
    <div className="u-col u-gap8" style={{ alignItems: "stretch" }}>
      {titleChanged && (
        <FieldChangeRow>
          <span className="field-name">タイトル</span>
          {before?.title ? (
            <>
              <span className="field-old">{before.title}</span>
              <span className="field-arrow material-symbols-rounded">
                arrow_forward
              </span>
            </>
          ) : null}
          <span>{after.title}</span>
        </FieldChangeRow>
      )}

      {changedFields.map(([key, label]) => (
        <FieldChangeRow key={key}>
          <span className="field-name">{label}</span>
          {before ? (
            <>
              <span className="field-old">
                {formatFieldValue(key, before[key])}
              </span>
              <span className="field-arrow material-symbols-rounded">
                arrow_forward
              </span>
            </>
          ) : null}
          <span>{formatFieldValue(key, after[key])}</span>
        </FieldChangeRow>
      ))}

      {contentChanged ? (
        <DiffBox>
          <DiffPre>
            {rows.map((row, i) => (
              <DiffLine key={i} $type={row.type}>
                {row.text}
              </DiffLine>
            ))}
          </DiffPre>
        </DiffBox>
      ) : (
        <p className="bodytext-11 u-text-light-gray">本文の変更はありません</p>
      )}
    </div>
  );
};

export default ChangeDiff;
